import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { FIT_LABEL_FLOOR_PX, FIT_LABEL_TRACKING_EM, stepsFrom, type FitTracking } from "./fitLabelSteps";

export { FIT_LABEL_FLOOR_PX };

/**
 * useFitLabel — size a single-line label to the width it actually has.
 *
 * Each label steps its OWN font-size down from `maxPx` (its design token size)
 * to the shared readable floor, then tries the floor with tight then tighter
 * letter-spacing, choosing the first step at which the text fits on one line.
 * Only if it still does not fit at floor+tighter does the caller wrap it. This
 * keeps every row single-line and dense without a global font change and
 * without hiding text behind an ellipsis.
 *
 * Measurement reuses the repo idiom (ScrollingText / useMeasuredBalance):
 * ResizeObserver + scrollWidth/clientWidth, rAF-debounced, equality-guarded so
 * it cannot thrash, and SSR/jsdom-safe (no layout -> keeps the max size).
 *
 * The measured element MUST fill its available width (`flex-1 min-w-0`) so its
 * clientWidth is the true space for the label and does not change when only the
 * font-size changes — that is what keeps the ResizeObserver free of a feedback
 * loop (font change never resizes the box it observes).
 */

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// `nowrap` is true ONLY after a measurement confirmed a single-line fit; the
// initial (unfitted) state is wrapped, so a label never renders nowrap before it
// is proven to fit (which is what could otherwise clip).
type FitState = { fontSize: number; tracking: FitTracking; nowrap: boolean };

export function useFitLabel(text: string, maxPx = 14) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const rafRef = useRef(0);
  const [fit, setFit] = useState<FitState>({
    fontSize: Math.max(Math.round(maxPx), FIT_LABEL_FLOOR_PX),
    tracking: "normal",
    nowrap: false
  });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const steps = stepsFrom(maxPx);
    const prevFontSize = el.style.fontSize;
    const prevWhiteSpace = el.style.whiteSpace;
    const prevLetterSpacing = el.style.letterSpacing;

    // Available width = the box while WRAPPING. A flex-1 label fills its track
    // and, because the text wraps, never grows past the real available width.
    el.style.whiteSpace = "normal";
    const avail = el.clientWidth;
    // No layout yet (SSR/jsdom/hidden flyout, clientWidth 0): we cannot confirm a
    // fit, so REVERT to the safe wrapped state. Leaving a stale nowrap:true here
    // means a hidden label revealed at a now-narrower width would paint nowrap
    // for one frame before the rAF re-measure — and with no overflow-hidden that
    // frame is a transient horizontal overflow. Equality-guarded: a no-op when
    // already wrapped, so it cannot loop.
    if (avail <= 0) {
      el.style.whiteSpace = prevWhiteSpace;
      setFit((prev) => (prev.nowrap ? { ...prev, nowrap: false } : prev));
      return;
    }
    el.style.whiteSpace = "nowrap";

    // Measure the TRUE single-line content width with a Range, NOT scrollWidth:
    // a flex-1 block that fills its track reports scrollWidth === clientWidth for
    // any content that fits, so a scrollWidth test would fail every fitting label
    // and step it to the floor (the "resized text" regression). A Range over the
    // text node gives the real glyph width regardless of the box.
    const range = document.createRange();
    range.selectNodeContents(el);

    let chosen = steps[steps.length - 1]!;
    let fits = false;
    for (const step of steps) {
      el.style.fontSize = `${step.px}px`;
      el.style.letterSpacing = FIT_LABEL_TRACKING_EM[step.tracking];
      // +1px safety: sub-pixel rounding must not pass a "fit" that renders one
      // glyph over the edge. Only a comfortably-fitting single line counts.
      if (range.getBoundingClientRect().width + 1 <= avail) {
        chosen = step;
        fits = true;
        break;
      }
      chosen = step; // remember floor+tighter if nothing fits
    }

    el.style.fontSize = prevFontSize;
    el.style.whiteSpace = prevWhiteSpace;
    el.style.letterSpacing = prevLetterSpacing;

    setFit((prev) =>
      prev.fontSize === chosen.px && prev.tracking === chosen.tracking && prev.nowrap === fits
        ? prev
        : { fontSize: chosen.px, tracking: chosen.tracking, nowrap: fits }
    );
  }, [maxPx]);

  useIsomorphicLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    // Re-measure on the next frame: the first pass can run before an animating
    // container (e.g. the mobile drawer sliding in) has settled the label's
    // track width, which would otherwise latch a too-wide fit until a resize.
    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
    };
    // Re-fit when the text changes (locale switch) or the available width
    // changes (collapse/expand, flyout open, container resize -> ResizeObserver).
  }, [measure, text]);

  return { ref, fontSize: fit.fontSize, tracking: fit.tracking, nowrap: fit.nowrap };
}
